// Meshy API service — image-to-3D avatar generation + auto-rigging
// Docs: https://docs.meshy.ai

import { gathrxrAuthHeader } from '../utils/quotaHeaders';

// Always the same-origin Pages Function: the key lives server-side only. In
// dev, Vite proxies /api to the live functions (see vite.config.ts), so no
// provider key is ever read by client code (and none can be inlined).
const MESHY_BASE = '/api/meshy';


function headers(): Record<string, string> {
  return { 'Content-Type': 'application/json' }; // key lives server-side
}

// ---- Types ----

export type MeshyTaskStatus = 'PENDING' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED' | 'CANCELED';

/** Generic task response — covers image-to-3d and text-to-3d */
export interface MeshyTask {
  id: string;
  status: MeshyTaskStatus;
  progress: number;
  model_urls?: { glb?: string; fbx?: string; obj?: string };
  texture_urls?: Array<{ base_color?: string; normal?: string; roughness?: string; metallic?: string }>;
  thumbnail_url?: string;
  created_at?: number;
  task_error?: { message?: string };
  // Rigging tasks use a different response shape
  result?: {
    rigged_character_glb_url?: string;
    rigged_character_fbx_url?: string;
    basic_animations?: {
      walking_glb_url?: string;
      running_glb_url?: string;
    };
    // Animation tasks
    animation_glb_url?: string;
    animation_fbx_url?: string;
  };
}

export interface ImageTo3DOptions {
  image_url: string;           // Public URL or base64 data URI
  ai_model?: string;           // 'meshy-6' (default) or older
  enable_pbr?: boolean;
  topology?: 'triangle' | 'quad';
  target_polycount?: number;
  should_remesh?: boolean;
}

export interface RiggingOptions {
  model_url?: string;          // GLB URL to rig
  input_task_id?: string;      // Or use a prior Meshy task ID
}

// ---- API calls ----

/** Create an image-to-3D task */
export async function createImageTo3D(opts: ImageTo3DOptions): Promise<string> {
  const res = await fetch(`${MESHY_BASE}/v1/image-to-3d`, {
    method: 'POST',
    headers: { ...headers(), ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({
      image_url: opts.image_url,
      // 'latest' rather than a pinned generation: a pinned id is a request
      // Meshy can start rejecting the day that model is retired, and the
      // failure mode is every generation 400ing while weeks-old avatars prove
      // the pipeline "used to work".
      ai_model: opts.ai_model || 'latest',
      enable_pbr: opts.enable_pbr ?? true,
      topology: opts.topology || 'triangle',
      target_polycount: opts.target_polycount || 30000,
      should_remesh: opts.should_remesh ?? true,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Meshy image-to-3D failed: ${res.status}`);
  }
  const data = await res.json();
  return data.result; // task ID
}

/** Create a text-to-3D preview task */
export async function createTextTo3D(prompt: string, negativePrompt?: string): Promise<string> {
  const res = await fetch(`${MESHY_BASE}/v2/text-to-3d`, {
    method: 'POST',
    headers: { ...headers(), ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({
      mode: 'preview',
      prompt,
      negative_prompt: negativePrompt || '',
      ai_model: 'meshy-6',
      topology: 'triangle',
      target_polycount: 30000,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Meshy text-to-3D failed: ${res.status}`);
  }
  const data = await res.json();
  return data.result;
}

/** Refine a text-to-3D preview */
export async function refineTextTo3D(previewTaskId: string): Promise<string> {
  const res = await fetch(`${MESHY_BASE}/v2/text-to-3d`, {
    method: 'POST',
    headers: { ...headers(), ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({
      mode: 'refine',
      preview_task_id: previewTaskId,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Meshy refine failed: ${res.status}`);
  }
  const data = await res.json();
  return data.result;
}

/** Create auto-rigging task */
export async function createRigging(opts: RiggingOptions): Promise<string> {
  const body: Record<string, string> = {};
  if (opts.input_task_id) body.input_task_id = opts.input_task_id;
  if (opts.model_url) body.model_url = opts.model_url;

  const res = await fetch(`${MESHY_BASE}/v1/rigging`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Meshy rigging failed: ${res.status}`);
  }
  const data = await res.json();
  return data.result;
}

/** Create an animation task (uses a completed rigging task + action_id from Animation Library) */
export async function createAnimation(rigTaskId: string, actionId: number): Promise<string> {
  const res = await fetch(`${MESHY_BASE}/v1/animations`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      rig_task_id: rigTaskId,
      action_id: actionId,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Meshy animation failed: ${res.status}`);
  }
  const data = await res.json();
  return data.result;
}

// Meshy Animation Library action IDs
export const MESHY_ANIMATIONS = {
  idle: 246,      // Idle_6 — calm neutral stand (Idle_9 hunches shoulders)
  walking: 1,     // Walking_Woman (Casual_Walk reads stiff on auto-rigs)
  running: 14,    // Run_02
  jumping: 466,   // Regular_Jump
} as const;

/** Poll a task by type and ID */
export async function getTask(type: 'image-to-3d' | 'text-to-3d' | 'rigging' | 'animation' | 'text-to-motion', taskId: string): Promise<MeshyTask> {
  const paths: Record<string, string> = {
    'image-to-3d': `${MESHY_BASE}/v1/image-to-3d/${taskId}`,
    'text-to-3d': `${MESHY_BASE}/v2/text-to-3d/${taskId}`,
    'rigging': `${MESHY_BASE}/v1/rigging/${taskId}`,
    'animation': `${MESHY_BASE}/v1/animations/${taskId}`,
    'text-to-motion': `${MESHY_BASE}/v1/text-to-motion/${taskId}`,
  };
  const res = await fetch(paths[type], { headers: headers() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Meshy get task failed: ${res.status}`);
  }
  return res.json();
}

/** Poll until a task succeeds or fails, calling onProgress along the way */
export async function pollTask(
  type: 'image-to-3d' | 'text-to-3d' | 'rigging' | 'animation' | 'text-to-motion',
  taskId: string,
  onProgress?: (progress: number, status: MeshyTaskStatus) => void,
  intervalMs = 5000,
  timeoutMs = 600000, // 10 min
): Promise<MeshyTask> {
  const start = Date.now();
  while (true) {
    const task = await getTask(type, taskId);
    onProgress?.(task.progress, task.status);

    if (task.status === 'SUCCEEDED') return task;
    if (task.status === 'FAILED' || task.status === 'CANCELED') {
      const errMsg = task.task_error?.message || 'Unknown error';
      throw new Error(`Task ${task.status}: ${errMsg}`);
    }
    if (Date.now() - start > timeoutMs) throw new Error('Task timed out');

    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/** Extract GLB URL from a task, handling different response shapes */
function getGlbUrl(task: MeshyTask, taskType: 'model' | 'rigging'): string | undefined {
  if (taskType === 'rigging') {
    // Rigging response: result.rigged_character_glb_url
    return task.result?.rigged_character_glb_url;
  }
  // Image-to-3D / Text-to-3D response: model_urls.glb
  return task.model_urls?.glb;
}

// ---- High-level pipeline ----

export interface AvatarPipelineCallbacks {
  onStage?: (stage: 'generating' | 'rigging' | 'animating' | 'uploading' | 'done') => void;
  onProgress?: (progress: number) => void;
}

/**
 * Full pipeline: image → 3D model → auto-rig → return GLB URL
 * Returns the Meshy-hosted rigged GLB URL (caller should re-upload to S3 for persistence).
 */
export async function generateAvatarFromImage(
  imageUrl: string,
  callbacks?: AvatarPipelineCallbacks,
): Promise<{ glbUrl: string; rigTaskId: string; thumbnailUrl?: string; idleGlbUrl?: string; walkingGlbUrl?: string; runningGlbUrl?: string; jumpingGlbUrl?: string }> {
  // Step 1: Image to 3D (0-60%)
  callbacks?.onStage?.('generating');
  const modelTaskId = await createImageTo3D({ image_url: imageUrl });

  const modelTask = await pollTask('image-to-3d', modelTaskId, (progress) => {
    callbacks?.onProgress?.(Math.round(progress * 0.6));
  });

  const modelGlb = getGlbUrl(modelTask, 'model');
  if (!modelGlb) throw new Error('No GLB model generated');

  // Step 2: Auto-rig (60-80%)
  callbacks?.onStage?.('rigging');
  const rigTaskId = await createRigging({ input_task_id: modelTaskId });

  const rigTask = await pollTask('rigging', rigTaskId, (progress) => {
    callbacks?.onProgress?.(60 + Math.round(progress * 0.2));
  });

  const riggedGlb = getGlbUrl(rigTask, 'rigging');
  if (!riggedGlb) throw new Error('Rigging did not produce a GLB');

  // Step 3: Generate all animations via Animation Library API (75-100%)
  callbacks?.onStage?.('animating');
  const animResults: Record<string, string | undefined> = {};
  const animEntries = Object.entries(MESHY_ANIMATIONS) as [string, number][];
  const totalAnims = animEntries.length;

  for (let i = 0; i < totalAnims; i++) {
    const [name, actionId] = animEntries[i];
    try {
      console.log(`[Meshy] Creating ${name} animation (action_id: ${actionId})`);
      const taskId = await createAnimation(rigTaskId, actionId);
      const task = await pollTask('animation', taskId, (progress) => {
        const base = 75 + (i / totalAnims) * 25;
        const slice = 25 / totalAnims;
        callbacks?.onProgress?.(Math.round(base + progress * slice));
      });
      animResults[name] = task.result?.animation_glb_url;
      console.log(`[Meshy] ${name} animation GLB:`, animResults[name]);
    } catch (err) {
      console.warn(`[Meshy] ${name} animation failed:`, err);
    }
  }

  callbacks?.onStage?.('done');
  return {
    glbUrl: riggedGlb,
    thumbnailUrl: modelTask.thumbnail_url,
    rigTaskId,
    idleGlbUrl: animResults.idle,
    walkingGlbUrl: animResults.walking,
    runningGlbUrl: animResults.running,
    jumpingGlbUrl: animResults.jumping,
  };
}

/**
 * Object pipeline: image → 3D model (PBR) → return GLB URL.
 * No rigging/animation — world props are static. Caller should re-upload the
 * GLB to S3 for persistence (Meshy URLs expire).
 */
export async function generateObjectFromImage(
  imageUrl: string,
  callbacks?: AvatarPipelineCallbacks,
): Promise<{ glbUrl: string; thumbnailUrl?: string }> {
  callbacks?.onStage?.('generating');
  const modelTaskId = await createImageTo3D({ image_url: imageUrl, enable_pbr: true });

  const modelTask = await pollTask('image-to-3d', modelTaskId, (progress) => {
    callbacks?.onProgress?.(Math.round(progress));
  });

  const glb = getGlbUrl(modelTask, 'model');
  if (!glb) throw new Error('No GLB model generated');

  callbacks?.onStage?.('done');
  return { glbUrl: glb, thumbnailUrl: modelTask.thumbnail_url };
}

/**
 * Full pipeline: text prompt → 3D preview → refine → auto-rig → return GLB URL
 */
export async function generateAvatarFromText(
  prompt: string,
  callbacks?: AvatarPipelineCallbacks,
): Promise<{ glbUrl: string; thumbnailUrl?: string; walkingGlbUrl?: string; runningGlbUrl?: string }> {
  // Step 1: Text to 3D preview
  callbacks?.onStage?.('generating');
  const previewId = await createTextTo3D(prompt);

  await pollTask('text-to-3d', previewId, (progress) => {
    callbacks?.onProgress?.(Math.round(progress * 0.4)); // 0-40%
  });

  // Step 2: Refine
  const refineId = await refineTextTo3D(previewId);

  const refineTask = await pollTask('text-to-3d', refineId, (progress) => {
    callbacks?.onProgress?.(40 + Math.round(progress * 0.3)); // 40-70%
  });

  const refinedGlb = getGlbUrl(refineTask, 'model');
  if (!refinedGlb) throw new Error('No GLB model from refinement');

  // Step 3: Auto-rig
  callbacks?.onStage?.('rigging');
  const rigTaskId = await createRigging({ input_task_id: refineId });

  const rigTask = await pollTask('rigging', rigTaskId, (progress) => {
    callbacks?.onProgress?.(70 + Math.round(progress * 0.3)); // 70-100%
  });

  const riggedGlb = getGlbUrl(rigTask, 'rigging');
  if (!riggedGlb) throw new Error('Rigging did not produce a GLB');

  const animations = rigTask.result?.basic_animations;

  callbacks?.onStage?.('done');
  return {
    glbUrl: riggedGlb,
    thumbnailUrl: refineTask.thumbnail_url,
    walkingGlbUrl: animations?.walking_glb_url,
    runningGlbUrl: animations?.running_glb_url,
  };
}

/** Generate then apply motion to the original rig; never apply raw FBX to a different skeleton. */
export async function generateTextMotion(rigTaskId: string, prompt: string, onStage: (message: string) => void): Promise<string> {
  if (!prompt.trim() || prompt.trim().length > 400) throw new Error('Describe the motion in 1–400 characters.');
  // Validate the rig before spending credits on a motion that cannot be applied.
  const rig = await getTask('rigging', rigTaskId);
  if (rig.status !== 'SUCCEEDED') throw new Error('The original Meshy rig is no longer available.');
  const post = async (path: string, body: object) => {
    const res = await fetch(`${MESHY_BASE}/${path}`, { method: 'POST', headers: { ...headers(), ...(await gathrxrAuthHeader()) }, body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || `Motion request failed (${res.status})`);
    if (typeof data.result !== 'string') throw new Error('Meshy did not return a task ID.');
    return data.result as string;
  };
  onStage('Generating motion...');
  const motionId = await post('v1/text-to-motion', { prompt: prompt.trim(), mode: 'prime', duration: 4 });
  await pollTask('text-to-motion', motionId, progress => onStage(`Generating motion: ${progress}%`));
  onStage('Applying motion to your character...');
  const animationId = await post('v1/animations', { rig_task_id: rigTaskId, motion_task_id: motionId });
  const task = await pollTask('animation', animationId, progress => onStage(`Applying motion: ${progress}%`));
  if (!task.result?.animation_glb_url) throw new Error('Meshy did not return an animated GLB.');
  return task.result.animation_glb_url;
}
